import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import { useQuery } from '@tanstack/react-query'
import { licenceNotice, type MemberLicence } from '@jupi/shared'
import { api } from '../api/client'
import { formatDate } from '../format'
import { useClubToday } from './ExpiryChip'

// #225: a member's own licence and medical, as a warning where it matters
// (the booking form). Recording them is optional, so having nothing on file
// is a reminder of the member's own legal responsibility, not an error.
// `profileLink` is the app's link to the profile, where they are kept up to
// date and the warning can be turned off (this package has no router).
export function LicenceNotice({ userId, profileLink }: { userId: string; profileLink?: ReactNode }) {
  const { t } = useTranslation()
  const today = useClubToday()
  const { data } = useQuery({
    queryKey: ['licences', userId],
    queryFn: () => api.get<{ licences: MemberLicence[] }>(`/api/licences/${userId}`),
  })
  if (!data) return null

  const notice = licenceNotice(data.licences, today)
  if (notice.missing) {
    return (
      <Alert severity="info">
        {t('licence.noticeMissing')}
        {profileLink ? <div>{profileLink}</div> : null}
      </Alert>
    )
  }
  if (notice.items.length === 0) return null

  const expired = notice.items.some((i) => i.status === 'expired')
  return (
    <Alert severity={expired ? 'error' : 'warning'}>
      {notice.items.map((i) => (
        <div key={`${i.licenceType}-${i.expiryDate}`}>
          {t(i.status === 'expired' ? 'licence.noticeExpired' : 'licence.noticeExpiring', {
            document: t(`licence.type_${i.licenceType}`),
            date: formatDate(i.expiryDate),
          })}
        </div>
      ))}
      <div>{t('licence.noticeResponsible')}</div>
      {profileLink ? <div>{profileLink}</div> : null}
    </Alert>
  )
}
